import memberApi, {
  MemberListResult,
  MemberFilterParams,
  CancelVIPMemberStatus,
  DeleteAccountParams,
} from "@api/memberApi";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { message } from "antd";
import { hideLoading, showLoading } from "./notifySlice";
import type { RootState } from "./rootReducer";

const getErrorMessage = (error: unknown) => (error instanceof Error ? error.message : "發生未知錯誤");

interface IState {
  isFetchingMemberList: boolean;
  memberListResult: MemberListResult;
  memberListParams: MemberFilterParams;
  checkOrderError: string;
  isDeleteAccountFetching: boolean | null;
}

const initialState: IState = {
  isFetchingMemberList: false,
  memberListResult: {
    count: 0,
    next: "",
    previous: "",
    results: [],
  },
  memberListParams: {
    offset: 0,
    limit: 20,
  },
  checkOrderError: "",
  isDeleteAccountFetching: null,
};

export const fetchMemberList = createAsyncThunk<MemberListResult, MemberFilterParams>(
  "member/fetchMemberList",
  async (params, thunkApi) => {
    try {
      const response = await memberApi.fetchMemberList(params);
      return response;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  },
);

export const cancelVIPAutoRenewal = createAsyncThunk<void, number>(
  "member/cancelVIPAutoRenewal",
  async (id, thunkApi) => {
    try {
      const {
        member: { memberListParams },
      } = thunkApi.getState() as RootState;
      await memberApi.cancelVIPAutoRenewal(id);
      thunkApi.dispatch(fetchMemberList(memberListParams));
    } catch (error: any) {
      thunkApi.rejectWithValue(error.message);
    }
  },
);

export const cancelVIPMember = createAsyncThunk<void, { id: number; status: CancelVIPMemberStatus }>(
  "member/cancelVIPMember",
  async (params, thunkApi) => {
    try {
      const {
        member: { memberListParams },
      } = thunkApi.getState() as RootState;
      const { id, status } = params;
      thunkApi.dispatch(showLoading());
      await memberApi.cancelVIPMember(id, status);
      message.success("取消成功");
      thunkApi.dispatch(fetchMemberList(memberListParams));
    } catch (error: any) {
      thunkApi.rejectWithValue(error.message);
    } finally {
      thunkApi.dispatch(hideLoading());
    }
  },
);

export const deleteAccount = createAsyncThunk("member/deleteAccount", async (params: DeleteAccountParams, thunkApi) => {
  try {
    const {
      member: { memberListParams },
    } = thunkApi.getState() as RootState;
    thunkApi.dispatch(showLoading());

    const response = await memberApi.deleteAccount(params);
    if (!params.checkOrder) {
      message.success("成功刪除帳戶");
    }
    thunkApi.dispatch(fetchMemberList(memberListParams));
    return response;
  } catch (error) {
    const errorMessage = getErrorMessage(error);
    message.error(errorMessage);
    return thunkApi.rejectWithValue(errorMessage);
  } finally {
    thunkApi.dispatch(hideLoading());
  }
});

const memberSlice = createSlice({
  name: "member",
  initialState,
  reducers: {
    resetMemberListParams: () => initialState,
    updateMemberListParams: (state, action: PayloadAction<MemberFilterParams>) => {
      state.memberListParams = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchMemberList.pending, (state) => {
      state.isFetchingMemberList = true;
    });
    builder.addCase(fetchMemberList.fulfilled, (state, action) => {
      state.isFetchingMemberList = false;
      state.memberListResult = action.payload;
    });
    builder.addCase(deleteAccount.rejected, (state, action) => {
      state.checkOrderError = action.payload as string;
    });
    builder.addCase(deleteAccount.pending, (state, action) => {
      state.isDeleteAccountFetching = true;
      state.checkOrderError = "";
    });
    builder.addCase(deleteAccount.fulfilled, (state, action) => {
      state.isDeleteAccountFetching = false;
      state.checkOrderError = "";
    });
  },
});

export const { resetMemberListParams, updateMemberListParams } = memberSlice.actions;
export default memberSlice.reducer;
